import React, { useState, useEffect, useMemo } from "react";
import { Drawer, Box, Checkbox, Divider, FormControlLabel } from "@mui/material";
import cross from './../../../assets/images/refresh-cw 1.svg';
import StarFilled from '../../../assets/images/StarFilled.svg';
import StarOutlined from '../../../assets/images/StarOutline.svg';
import SnackBarMessage from "../snackbar/snackbar";
import CustomButton from "../../components/buttons/CustomButton";
import styles from "./CustomiseColumns.module.css";
import { saveFilterPreference } from "../../api/api";

const MoreFilters = ({ anchor, filterDrawer, handleFilterDrawer, rmpmFilters, setRmpmFilters, username, type, options, onFiltersSaved, viewType, limit = 9 }) => {
    // console.log("RMPM Filters in MoreFilters:", options);
    const [localFilters, setLocalFilters] = useState([...rmpmFilters]);
    const [selectedKey, setSelectedKey] = useState(rmpmFilters[0].label);
    const [displayData, setDisplayData] = useState(
        localFilters.filter((filter) => filter.isFavourite)
    );
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: ' ',
        type: 'error'
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setLocalFilters([...rmpmFilters]);
    }, [rmpmFilters]);

    // Update displayData whenever localFilters changes
    useEffect(() => {
        const favoriteFilters = localFilters.filter((filter) => filter.isFavourite)
        setDisplayData(favoriteFilters);
    }, [localFilters]);

    const closeDrawer = () => handleFilterDrawer();

    const closeSnackbar = () => {
        setSnackBarObject({ message: '', snackbarOpen: false, type: 'error' });
    };

    const handleFavoriteUpdate = (index) => {
        setLocalFilters((prevFilters) => {
            const currentFavoritesCount = prevFilters.filter((filter) => filter.isFavourite).length;
            const isAddingFavorite = !prevFilters[index].isFavourite;

            if (isAddingFavorite && currentFavoritesCount >= limit) {
                setSnackBarObject({
                    snackbarOpen: true,
                    message: `You can't add more than ${limit} favorite filters.`,
                    type: "error",
                });
                return prevFilters; // Return the previous filters without updating
            }

            const updatedFilters = prevFilters.map((filter, i) =>
                i === index ? { ...filter, isFavourite: !filter.isFavourite } : filter
            );

            return updatedFilters;
        });
    };

    const handleFilterSelectionChange = (value) => {
        setLocalFilters((prevFilters) => {
            return prevFilters.map((filter) => {
                if (filter.label === selectedKey) {
                    return {
                        ...filter,
                        selectedData: filter.label!=='PO Ageing'?(filter.selectedData.includes(value)
                            ? filter.selectedData.filter((item) => item !== value)
                            : [...filter.selectedData, value]):[value]
                    };
                }
                return filter;
            });
        });
    };

    const savePreferencesToServer = async () => {
        setIsSaving(true);
        try {
            const filters = localFilters.map(filter => ({
                label: filter.label,
                isFavourite: Boolean(filter.isFavourite)
            }));

            const payload = {
                email: username,
                type: viewType === 'MAI View' ? 'MAI' :viewType==='Open PO Details'?'OpenPO Details':viewType==='FG Coverage'?viewType:viewType==='Stock to Norm'?'Norm Alert':type,
                filters: filters,
            };

            const response = await saveFilterPreference(
                payload.email,
                payload.type,
                payload.filters,
            );

            if (response && response.success) {
                setSnackBarObject({
                    snackbarOpen: true,
                    message: "Preferences saved successfully!",
                    type: "success",
                });
                return true;
            } else {
                throw new Error(response?.message || "Failed to save preferences");
            }
        } catch (error) {
            console.error('Save error:', error);
            setSnackBarObject({
                snackbarOpen: true,
                message: error.message || "Failed to save preferences",
                type: "error",
            });
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleApply = async () => {
        const saveSuccess = await savePreferencesToServer();

        if (saveSuccess) {
            setRmpmFilters([...localFilters]);
            if (onFiltersSaved) {
                onFiltersSaved([...localFilters]);
            }
            closeDrawer();
        }
    };

    const getCurrentValues = () => {
        if (selectedKey === "Alert status" &&
            ["RMPM Alert", "Contract Alert", "Agile OR"].includes(type)) {
            return ["Open", "Accepted", "Rejected"];
        }
        let values = [];

        if (options && Object.keys(options).length > 0) {
            values = options[selectedKey] || [];
        } else if (type === "RMPM Alert" && viewType === "MAI View") {
            values = localFilters[selectedKey]?.data || [];
        } else {
            const filter = localFilters.find(f => f?.label === selectedKey);
            values = filter?.data || [];
        }
        // Filter out "Contract Available" if the selected key is "Root cause"
        if (selectedKey === "Root cause" && type === "Contract Alert") {
            return values.filter(value => value !== "Contract Available");
        }

        return values;
    };

    const currentValues = getCurrentValues();

    return (
        <div className={styles.drawerContainer}>
            <Drawer
                anchor={anchor}
                open={filterDrawer}
                sx={{ '& .MuiDrawer-paper': { width: "35vw" } }}
            >
                {/* Drawer content */}
                <div className={styles.heading}>
                    <span>More Filters</span>
                    <img src={cross} className={styles.closeIcon} onClick={closeDrawer} alt="Close drawer" />
                </div>

                <div className={styles.drawerContent}>
                    <div className={styles.contentText}>
                        Explore more filter options in this panel. Click the star icon to mark a filter as a favorite - up
                        to {limit} filters will appear on the landing page. <br />
                        &nbsp;• To update favorites, uncheck a starred filter and select a new one. <br />
                    </div>
                </div>

                <Box className={styles.filterContainer}>
                    <Box className={styles.keyContainer}>
                        {localFilters.map((filter, index) => {
                            const isFavorite = displayData.some((favFilter) => favFilter.label === filter.label);
                            return (
                                <span
                                    key={index}
                                    className={`${styles.filterKeys} ${selectedKey === filter.label ? styles.selectedKey : ""}`}
                                    onClick={() => setSelectedKey(filter.label)}
                                >
                                    <img
                                        src={isFavorite ? StarFilled : StarOutlined}
                                        alt="Star Icon"
                                        className={styles.starIcon}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleFavoriteUpdate(index);
                                        }}
                                    />
                                    {filter.label}
                                </span>
                            );
                        })}
                    </Box>

                    <Divider orientation="vertical" className={styles.divider} />

                    <Box className={styles.valueContainer}>
                        {currentValues.map((value, i) => (
                            <FormControlLabel
                                key={i}
                                sx={{ margin: "0", padding: "5px 15px", '& .MuiFormControlLabel-label': { fontSize: '12px', marginLeft: '5px' } }}
                                control={
                                    <Checkbox
                                        sx={{ transform: "scale(0.8)", padding: '0' }}
                                        checked={localFilters.find(f => f.label === selectedKey)?.selectedData?.includes(value) || false}
                                        onChange={() => handleFilterSelectionChange(value)}
                                    />
                                }
                                label={value}
                            />
                        ))}
                    </Box>
                </Box>
                <div className={styles.drawerBtns}>
                    <CustomButton label="Cancel" type="mm-save" onClick={closeDrawer} />
                    <CustomButton
                        label={isSaving ? "Saving..." : "Apply"}
                        type="mm-submit"
                        onClick={handleApply}
                        disabled={isSaving}
                    />
                </div>
            </Drawer>
            {snackBarObject["snackbarOpen"] && (
                <SnackBarMessage
                    type={snackBarObject["type"]}
                    snackbarMessage={snackBarObject["message"]}
                    snackbarOpen={snackBarObject["snackbarOpen"]}
                    setSnackbarOpen={closeSnackbar}
                />
            )}
        </div>
    );
};

export default MoreFilters;